// @flow

import React, { useEffect, useRef } from 'react';
import useOutsideClick from 'core/hooks/useOutsideClick';
import useSetState from 'core/hooks/useSetState';
import { jobs as t } from 'helpers/translation';
import delay from 'helpers/delay';
import store from '../store';

type Props = {
  placeholder: string,
  handleChange: Function,
  defaultPlaceId: number | string,
  places: any,
  search: any
};

type StateProps = {
  value: string | number,
  prevValue: string | number,
  dropdownOpened: boolean,
  dropdownItems: any,
  placesFetched: boolean,
  placeChosen: boolean,
  prevPlaceId: string | number
};

const delayFetchTimeout = 700;
const delayFetch = delay();

export default ({ placeholder, handleChange, defaultPlaceId, places, search: { place_id } }: Props) => {
  const { getPlaces, getPlaceName, placeName, placeFetching, placesFetching } = store;
  const [{ value, prevValue, dropdownOpened, dropdownItems, placesFetched, placeChosen, prevPlaceId }, setState] = useSetState<StateProps>({
    value: '',
    prevValue: '',
    dropdownOpened: false,
    dropdownItems: [],
    placesFetched: false,
    placeChosen: false,
    prevPlaceId: ''
  });

  useEffect(() => {
    if (!value && !placeName && defaultPlaceId || defaultPlaceId && prevPlaceId !== defaultPlaceId) {
      getPlaceName(defaultPlaceId);
      setState({ prevPlaceId: defaultPlaceId, placeChosen: true });
    }

    setState({ value: placeName, prevValue: placeName });
  }, [placeName, defaultPlaceId]);

  useEffect(() => {
    setState({ value: defaultPlaceId ? value : '' });
  }, [defaultPlaceId]);

  useEffect(() => {
    setState({ dropdownItems: places });
  }, [places]);

  useEffect(() => {
    setState({ placesFetched: !placesFetching });
  }, [placesFetching]);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useOutsideClick(dropdownRef, () => {
    setState({ dropdownOpened: false, value: placeChosen && place_id ? prevValue : '' });
  });

  const openDropdown = () => {
    if (inputRef.current) inputRef.current.focus();
    setState({ dropdownOpened: true, value: '', dropdownItems: [], placesFetched: false });
  };

  const inputChange = ({ target }) => {
    delayFetch(() => {
      if (target.value.length >= 3) {
        getPlaces({ filter: { query: target.value } });
        setState({ placesFetched: true });
      }
    }, delayFetchTimeout);

    setState({ value: target.value, dropdownOpened: true });
  };

  const handleClick = ({ target }) => {
    handleChange(target.attributes.value.value);

    setState({
      value: target.attributes.name.value,
      prevValue: target.attributes.name.value,
      placeChosen: true,
      dropdownOpened: false
    });
  };

  const renderItems = () => (
    dropdownItems.length
      ? dropdownItems.map(({ id, name }) => (
        <li
          key={id}
          value={id}
          name={name}
          onClick={handleClick}
          role="presentation"
        >
          {name}
        </li>
      )
      )
      : placesFetched && <li>{ t.no_places_found }</li>
  );

  return (
    <div className="filter__autocomplete" ref={dropdownRef}>
      <input
        className="autocomplete__input reset"
        value={placeFetching ? '' : value}
        placeholder={placeholder}
        ref={inputRef}
        onChange={inputChange}
        onClick={openDropdown}
      />
      <div className={`autocomplete__result autocomplete_search${dropdownOpened ? ' opened' : ''}`}>
        <ul>
          {renderItems()}
        </ul>
      </div>
    </div>
  );
};
